import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from 'src/app/components/_alert/alert.service';
import * as Auth from 'aws-amplify/auth';
import QRCode from 'qrcode';

interface FormValues {
  codigo: string;
}

@Component({
  selector: 'app-mfa-config',
  templateUrl: './mfa-config.component.html',
  styleUrls: ['./mfa-config.component.scss']
})
export class MfaConfigComponent {
  public qrCodeData: string = '';
  public sharedSecret: string = '';
  public username: string = '';
  public showSetup: boolean = false;
  public statusMessage: string = '';
  public qrCodeDataUrl: string | any = null;
  public form: UntypedFormGroup;

  public showMessage: boolean = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly titleService: Title,
    private formBuilder: UntypedFormBuilder,
    public alertService: AlertService,
    private readonly router: Router
  ) {
    this.form = this.formBuilder.group({
      codigo: ['', [Validators.required]],
    }) as FormGroupTyped<FormValues>;
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle(this.route.snapshot.data['title']);
    const { accessToken, idToken } = (await Auth.fetchAuthSession()).tokens ?? {}; // se valida que tenga token
    if(idToken) {
      this.checkMfaStatus();
    } else {
      this.router.navigate(['/login']);
    }
  }

  async checkMfaStatus() {
    try {
      const user = await Auth.getCurrentUser();
      this.username = user.username;

      const mfaConfig = await Auth.fetchMFAPreference();

      const canConfigureTotp =
        !mfaConfig?.preferred || mfaConfig?.preferred === 'TOTP';

      if (canConfigureTotp) {
        await this.iniciarSetupTotp();
      } else {
        this.alertService.warn('Actualmente tienes otro método MFA configurado y no puedes usar TOTP.');
      }

    } catch (err) {
      this.alertService.error('Error al cargar la configuración.');
      console.error('MFA setup error:', err);
    }
  }

  async iniciarSetupTotp() {
    try {
      const secret = await Auth.setUpTOTP();
      this.sharedSecret = secret.sharedSecret;

      const issuer = 'InformaxClientes';
      this.qrCodeData = `otpauth://totp/${issuer}:${this.username}?secret=${this.sharedSecret}&issuer=${issuer}`;
      await this.generateQRCode();

      this.showSetup = true;
    } catch (err: any) {
      if (err.name === 'InvalidParameterException' || err.message?.includes('not enabled')) {
        this.alertService.error('La autenticación de dos factores (MFA) está desactivada en este sistema.');
      } else {
        this.alertService.error('Error al iniciar la configuración de MFA.');
      }
    }
  }

  async verifyCode() {
    const { codigo } = this.form.value;
    if (this.form.valid) {
      try {
        await Auth.verifyTOTPSetup({ code: codigo });
        await Auth.updateMFAPreference({ totp: 'PREFERRED' });
        this.showSetup = false;
        this.showMessage = true;
        //esperar 5 seg y redireccionar al inicio
        setTimeout(() => {
          this.redireccionInicio();
        }, 5000);
      } catch (error) {
        this.alertService.error("Error al verificar el código TOTP. Asegúrate de que el código sea correcto y no haya expirado.");
      }
    } else {
      this.alertService.warn("Código no válido");
    }

  }

  async generateQRCode() {
    this.qrCodeDataUrl = await QRCode.toDataURL(this.qrCodeData);
  }

  resetPage(): void {
    this.showMessage = false;
    this.qrCodeData = '';
    this.sharedSecret = '';
    this.username = '';
    this.showSetup = false;
    this.statusMessage = '';
    this.qrCodeDataUrl = null;
    this.clearForm();
    this.checkMfaStatus();
  }

  clearForm(): void {
    this.form.reset();
  }

  redireccionInicio(): void {
    this.router.navigate(['']);
  }

}
